import * as React from 'react';
import { makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';
import useStatusCalendar from './useStatusCalendar';
import { select, timeDay } from 'd3';
import { fontFamilies } from '../theme/text';
import { neutral } from '../theme/colors';

type Status = 'success' | 'fail' | 'warn' | null;

/** Data point for a single status */
export interface StatusCalendarDatum {
  date: Date;
  /** Status, mapped to the color of the representative square. Null maps to gray and does not show a tooltip. */
  status: Status;
  /** Text shown in the third line of the tooltip on hover. Intended to be very short summary, i.e. 'Result: 10' */
  description: string;
}

/** Type of tooltip data: Datum + the x and y coordinates of the corresponding chart element */
export interface Tooltip extends StatusCalendarDatum {
  x: number;
  y: number;
}

export interface StatusCalendarProps {
  /** Array of data objects with a key for each area */
  data: StatusCalendarDatum[];
  /** className for div container overrides */
  containerClassName?: string;
  /** width attribute of the svg element */
  svgWidth?: number | string;
  /** height attribute of the svg element */
  svgHeight?: number | string;
}

const useStyles = makeStyles<Theme, { svgWidth: number | string; svgHeight: number | string }>(
  (theme: Theme) => ({
    root: {
      display: 'flex',
      width: '100%',
      height: '100%'
    },
    svg: ({ svgHeight, svgWidth }) => ({
      fontFamily: fontFamilies.body,
      height: svgHeight,
      width: svgWidth
    }),
    lines: {
      fill: 'none',
      strokeWidth: 1,
      strokeLinejoin: 'round',
      strokeLinecap: 'round'
    },
    tooltip: {
      '& path': {
        fill: neutral[100],
        stroke: neutral[400]
      },
      '& rect': {
        fill: neutral[100],
        stroke: neutral[400]
      }
    },
    tooltipText: {
      fontSize: 12,
      textAnchor: 'middle'
    },
    rect: {
      '&:hover': {
        opacity: '50%'
      },
      animationName: '$spin',
      animationFillMode: 'both',
      animationDuration: '4s',
      animationPlayState: 'paused'
    },
    '@keyframes spin': {
      '0%': { transform: 'rotate(0)' },
      '100%': { transform: 'rotate(360deg)' }
    }
  })
);

/**
 * Function to ensure the dataset is sorted and has 180 days of data.
 * If longer, show the last 180 days. If shorter, add filler days to display inactive days where no data exists.
 */
const formatData = (data: StatusCalendarDatum[]) => {
  // format date strings into Date objects
  let formatted = data
    .map((d) => ({
      date: new Date(d.date),
      status: d.status,
      description: d.description
    }))
    .sort((a, b) => {
      if (a.date > b.date) {
        return 1;
      } else if (a.date < b.date) {
        return -1;
      } else {
        return 0;
      }
    });

  const earliestDate = timeDay.offset(new Date(), -180);
  // Remove any dates before 180 days ago, if needed
  if (formatted[0].date < earliestDate || formatted.length > 180) {
    formatted = formatted.filter((d) => d.date >= earliestDate);
  }

  if (formatted.length < 180) {
    // if missing days, fill with status: null days
    const dateFill = timeDay.range(earliestDate, new Date());
    // Interpolate existing days and filler days
    return dateFill.map((day) => {
      return formatted.length > 0 && formatted[0].date.valueOf() == day.valueOf()
        ? formatted.shift()
        : { date: day, status: null, description: '' };
    });
  }

  return formatted;
};

/**
 * Render a status calendar -- a compact historical view of status data.
 *
 * The chart shows 180 days of data. If there is less it will fill with null
 * values up to the earliest date in the set. If there is more it will truncate
 * to the most recent 180 days.
 *
 * Currently time is handled using the local time settings in d3-time and
 * timezones of the incoming data are ignored. UTC time could be handled
 * with UTC specific d3-time functions.
 */
const StatusCalendar = ({ data, containerClassName, svgHeight, svgWidth }: StatusCalendarProps) => {
  const [currentData, setCurrentData] = React.useState<StatusCalendarDatum[]>(formatData(data));
  const {
    cellSize,
    x,
    y,
    color,
    dayAxis,
    monthAxis,
    viewBoxWidth,
    viewBoxHeight
  } = useStatusCalendar(currentData);
  const classes = useStyles({ svgHeight, svgWidth });
  const gridRef = React.useRef<SVGGElement>();
  const dayAxisRef = React.useRef<SVGGElement>();
  const monthAxisRef = React.useRef<SVGGElement>();
  const tooltipTextRef = React.useRef<SVGGElement>();
  const [tooltip, setTooltip] = React.useState<Tooltip>();
  const [tooltipBox, setTooltipBox] = React.useState<{
    width: number;
    height: number;
    translateX: number;
    translateY: number;
  }>({
    width: 0,
    height: 0,
    translateX: 0,
    translateY: 0
  });
  const dateFormat: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric'
  };

  React.useEffect(() => {
    setCurrentData(formatData(data));
  }, [data]);

  React.useEffect(() => {
    select(gridRef.current)
      .selectAll('rect')
      .data(currentData)
      .join('rect')
      .attr('width', cellSize * 0.8)
      .attr('height', cellSize * 0.8)
      .attr('x', x)
      .attr('y', y)
      .attr('fill', color)
      .attr('class', classes.rect)
      .attr('transform-origin', (d) => `${x(d) + cellSize / 2} ${y(d) + cellSize / 2}`)
      .style('animation-delay', (_, i) => `${i * 20}ms`)
      .on('mouseover', (_, d) => {
        d.status && setTooltip({ ...d, x: x(d), y: y(d) });
      })
      .on('mouseout', () => setTooltip(null));
  }, [gridRef, currentData]);

  React.useEffect(() => {
    select(monthAxisRef.current).call(monthAxis);
  }, [monthAxisRef, currentData]);

  React.useEffect(() => {
    select(dayAxisRef.current).call(dayAxis);
  }, [dayAxisRef]);

  React.useEffect(() => {
    if (tooltip) {
      const box = tooltipTextRef.current.getBBox();
      const width = box.width + 6;
      const height = box.height + 2;
      let translateX: number;
      if (tooltip.x < width / 2) {
        translateX = 4;
      } else if (viewBoxWidth - tooltip.x < width / 2 + cellSize * 0.4) {
        translateX = viewBoxWidth - width - 4;
      } else {
        translateX = tooltip.x - width / 2 + cellSize * 0.4;
      }
      const translateY =
        tooltip.y < viewBoxHeight / 2 ? tooltip.y + cellSize : tooltip.y - height - 3;
      setTooltipBox({ width, height, translateX, translateY });
    }
  }, [tooltip]);

  function getStatusText(status: Status) {
    switch (status) {
      case 'success':
        return 'Pass';
      case 'fail':
        return 'Fail';
      case 'warn':
        return 'Warn';
      default:
        return '';
    }
  }

  return (
    <div className={clsx(classes.root, containerClassName)}>
      <svg className={classes.svg} viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}>
        <defs>
          <filter id="tooltip-shadow">
            <feDropShadow dx="0" dy="1.5" stdDeviation="2" floodColor={neutral[600]} />
          </filter>
        </defs>
        <g ref={gridRef}></g>
        <g ref={dayAxisRef}></g>
        <g ref={monthAxisRef}></g>
        {tooltip && (
          <g
            className={classes.tooltip}
            transform={`translate(${tooltipBox.translateX} ${tooltipBox.translateY})`}
            style={{ filter: 'url(#tooltip-shadow)' }}
          >
            <rect width={tooltipBox.width} height={tooltipBox.height} rx="2" />
            <g ref={tooltipTextRef}>
              <text
                x={tooltipBox.width / 2}
                y="4"
                dominantBaseline="hanging"
                fontWeight="600"
                className={classes.tooltipText}
              >
                {tooltip.date.toLocaleString('en-US', dateFormat)}
              </text>
              <text
                x={tooltipBox.width / 2}
                y="16"
                dominantBaseline="hanging"
                className={classes.tooltipText}
              >
                {getStatusText(tooltip.status)}
              </text>
              <text
                x={tooltipBox.width / 2}
                y="28"
                dominantBaseline="hanging"
                className={classes.tooltipText}
              >
                {tooltip.description}
              </text>
            </g>
          </g>
        )}
      </svg>
    </div>
  );
};

export default StatusCalendar;
